import React, { useState, useEffect, useCallback, useMemo } from "react";
import { ExpenseApplyScope, expensesApi } from "../../../services/expensesApi";
import { Expense } from "../../../types";
import { AdminPageHeader } from "../shared";
import { Icons } from "../../shared/Icons";
import {
  Loading,
  ErrorState,
  ActionButton,
  Modal,
  ConfirmationModal,
  FormActions,
  Input,
  Select,
} from "../../shared";
import { useToaster } from "../../shared/Toaster/Toaster";
import { formatMonthYearLong, getTodayMonthYearBrazil } from "../../../utils/dateUtils";
import { formatBalanceCurrency } from "../AdminDashboard/balanceShared";

const EXPENSE_CATEGORIES = [
  "Aluguel",
  "Energia",
  "Água",
  "Internet",
  "Funcionário",
  "Material",
  "Manutenção",
  "Marketing",
  "Equipamento",
  "Outros",
];

const RECURRENT_SUGGESTIONS: { description: string; category: string }[] = [
  { description: "Aluguel", category: "Aluguel" },
  { description: "Energia elétrica", category: "Energia" },
  { description: "Água", category: "Água" },
  { description: "Internet", category: "Internet" },
  { description: "Salário de funcionário", category: "Funcionário" },
  { description: "Material", category: "Material" },
];

const RECURRENCE_TYPES = [
  { value: "monthly", label: "Mensal" },
  { value: "weekly", label: "Semanal" },
  { value: "yearly", label: "Anual" },
];

interface ExpenseFormData {
  description: string;
  amount: string;
  category: string;
  expenseDate: string;
  isRecurrent: boolean;
  recurrenceType: string;
  notes: string;
}

const EMPTY_FORM: ExpenseFormData = {
  description: "",
  amount: "",
  category: "",
  expenseDate: new Date().toISOString().slice(0, 10),
  isRecurrent: false,
  recurrenceType: "monthly",
  notes: "",
};

function formatDateBr(date: string): string {
  if (!date) return "";
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`;
}

export const AdminExpenses: React.FC = () => {
  const { showToast } = useToaster();
  const [monthYear, setMonthYear] = useState(() => getTodayMonthYearBrazil());
  const { month, year } = monthYear;

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [form, setForm] = useState<ExpenseFormData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteScopeModalOpen, setDeleteScopeModalOpen] = useState(false);
  const [editScopeModalOpen, setEditScopeModalOpen] = useState(false);
  const [pendingEditPayload, setPendingEditPayload] = useState<Record<string, unknown> | null>(null);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await expensesApi.list({ month, year });
      if (res.success && res.data) {
        setExpenses(res.data);
      } else {
        setError(res.error || "Erro ao carregar despesas");
      }
    } catch {
      setError("Erro ao carregar despesas");
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const goPrevMonth = () =>
    setMonthYear(({ month: m, year: y }) =>
      m <= 1 ? { month: 12, year: y - 1 } : { month: m - 1, year: y }
    );
  const goNextMonth = () =>
    setMonthYear(({ month: m, year: y }) =>
      m >= 12 ? { month: 1, year: y + 1 } : { month: m + 1, year: y }
    );

  const openCreate = (prefill?: Partial<ExpenseFormData>) => {
    setEditingExpense(null);
    setForm({ ...EMPTY_FORM, ...prefill });
    setModalOpen(true);
  };

  const openEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setForm({
      description: expense.description,
      amount: String(expense.amount),
      category: expense.category || "",
      expenseDate: expense.expenseDate,
      isRecurrent: expense.isRecurrent,
      recurrenceType: expense.recurrenceType || "monthly",
      notes: expense.notes || "",
    });
    setModalOpen(true);
  };

  const persistSave = async (
    payload: {
      description: string;
      amount: number;
      category: string | null;
      expenseDate: string;
      isRecurrent: boolean;
      recurrenceType: string | null;
      notes: string | null;
    },
    scope: ExpenseApplyScope = "single"
  ) => {
    if (!editingExpense) {
      const res = await expensesApi.create(payload);
      if (res.success) {
        showToast("success", "Despesa registrada");
        setModalOpen(false);
        fetchExpenses();
      } else {
        showToast("error", res.error || "Erro ao registrar despesa");
      }
      return;
    }

    const res = await expensesApi.update(editingExpense.id, payload, scope);
    if (res.success) {
      showToast("success", "Despesa atualizada");
      setModalOpen(false);
      fetchExpenses();
    } else {
      showToast("error", res.error || "Erro ao atualizar despesa");
    }
  };

  const handleSave = async () => {
    if (!form.description.trim()) {
      showToast("error", "Descrição é obrigatória");
      return;
    }
    const parsedAmount = parseFloat(form.amount.replace(",", "."));
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      showToast("error", "Valor inválido");
      return;
    }
    if (!form.expenseDate) {
      showToast("error", "Data é obrigatória");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        description: form.description.trim(),
        amount: parsedAmount,
        category: form.category || null,
        expenseDate: form.expenseDate,
        isRecurrent: form.isRecurrent,
        recurrenceType: form.isRecurrent ? form.recurrenceType : null,
        notes: form.notes.trim() || null,
      };

      if (editingExpense?.isRecurrent && editingExpense.recurrenceGroup) {
        setPendingEditPayload(payload);
        setEditScopeModalOpen(true);
        return;
      }

      await persistSave(payload, "single");
    } finally {
      setSaving(false);
    }
  };

  const applyEditScope = async (scope: ExpenseApplyScope) => {
    if (!pendingEditPayload) return;
    setSaving(true);
    try {
      await persistSave(
        pendingEditPayload as {
          description: string;
          amount: number;
          category: string | null;
          expenseDate: string;
          isRecurrent: boolean;
          recurrenceType: string | null;
          notes: string | null;
        },
        scope
      );
      setEditScopeModalOpen(false);
      setPendingEditPayload(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scope: ExpenseApplyScope = "single") => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await expensesApi.remove(deleteTarget.id, scope);
      if (res.success) {
        showToast("success", "Despesa excluída");
        setDeleteTarget(null);
        setDeleteScopeModalOpen(false);
        fetchExpenses();
      } else {
        showToast("error", "Erro ao excluir despesa");
      }
    } finally {
      setDeleting(false);
    }
  };

  const askDelete = (expense: Expense) => {
    setDeleteTarget(expense);
    if (expense.isRecurrent && expense.recurrenceGroup) {
      setDeleteScopeModalOpen(true);
    } else {
      setDeleteScopeModalOpen(false);
    }
  };

  const totalExpenses = useMemo(
    () => expenses.reduce((sum, e) => sum + Number(e.amount), 0),
    [expenses]
  );

  const byCategory = useMemo(() => {
    const map: Record<string, number> = {};
    for (const e of expenses) {
      const cat = e.category || "Outros";
      map[cat] = (map[cat] || 0) + Number(e.amount);
    }
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [expenses]);

  return (
    <div className="space-y-4 sm:space-y-6 md:space-y-8 animate-fade-in">
      <AdminPageHeader
        icon={<Icons.TrendingDown sx={{ fontSize: 28 }} />}
        title="Despesas"
        subtitle="Gerencie os gastos do seu negócio"
        rightContent={
          <ActionButton
            variant="primary"
            size="sm"
            icon={<Icons.Add sx={{ fontSize: 20 }} />}
            onClick={() => openCreate()}
          >
            Nova Despesa
          </ActionButton>
        }
      />

      {/* Month navigator */}
      <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={goPrevMonth}
            className="p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Icons.ChevronLeft sx={{ fontSize: 24 }} />
          </button>
          <span className="text-base sm:text-lg font-semibold text-gray-900">
            {formatMonthYearLong(month, year)}
          </span>
          <button
            type="button"
            onClick={goNextMonth}
            className="p-2 rounded-lg border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Icons.ChevronRight sx={{ fontSize: 24 }} />
          </button>
        </div>
      </div>

      {/* Recurrent suggestions */}
      <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl p-3 sm:p-4">
        <p className="text-xs sm:text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2 sm:mb-3">
          Despesas recorrentes sugeridas
        </p>
        <div className="flex flex-wrap gap-2">
          {RECURRENT_SUGGESTIONS.map((s) => (
            <button
              key={s.description}
              type="button"
              onClick={() =>
                openCreate({
                  description: s.description,
                  category: s.category,
                  isRecurrent: true,
                  recurrenceType: "monthly",
                })
              }
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/60 transition-colors font-medium"
            >
              <Icons.Add sx={{ fontSize: 14 }} />
              {s.description}
            </button>
          ))}
        </div>
      </div>

      {/* Summary card */}
      {!loading && !error && expenses.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950 dark:to-rose-950 rounded-xl border border-red-200 dark:border-red-800 p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Total de Despesas</p>
                <p className="text-2xl sm:text-3xl font-bold text-red-700 dark:text-red-400">
                  {formatBalanceCurrency(totalExpenses)}
                </p>
                <p className="text-xs text-gray-500 mt-1">{expenses.length} lançamento(s)</p>
              </div>
              <Icons.TrendingDown sx={{ fontSize: 36, color: "#be123c" }} />
            </div>
          </div>

          {byCategory.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 sm:p-6">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-3">Por Categoria</p>
              <div className="space-y-2 max-h-36 overflow-y-auto">
                {byCategory.map(([cat, total]) => (
                  <div key={cat} className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs text-gray-700 truncate">{cat}</span>
                        <span className="text-xs font-semibold text-gray-900 ml-2 shrink-0">
                          {formatBalanceCurrency(total)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className="bg-red-400 h-1.5 rounded-full"
                          style={{ width: `${totalExpenses > 0 ? (total / totalExpenses) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} />
      ) : expenses.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 p-8 text-center">
          <Icons.ShoppingCart sx={{ fontSize: 40, color: "#9ca3af" }} />
          <p className="mt-3 text-sm text-gray-500">Nenhuma despesa registrada neste mês</p>
          <p className="text-xs text-gray-400 mt-1">
            Clique em "Nova Despesa" para começar
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 overflow-hidden">
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {expenses.map((expense) => (
              <div
                key={expense.id}
                className="flex items-center gap-3 p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {expense.description}
                    </span>
                    {expense.isRecurrent && (
                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                        <Icons.Repeat sx={{ fontSize: 12 }} />
                        {RECURRENCE_TYPES.find((r) => r.value === expense.recurrenceType)?.label ||
                          "Recorrente"}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {expense.category && (
                      <span className="text-xs text-gray-500">{expense.category}</span>
                    )}
                    <span className="text-xs text-gray-400">{formatDateBr(expense.expenseDate)}</span>
                    {expense.notes && (
                      <span className="text-xs text-gray-400 italic truncate max-w-xs">
                        {expense.notes}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-sm sm:text-base font-bold text-red-700 shrink-0">
                  {formatBalanceCurrency(Number(expense.amount))}
                </span>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(expense)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    title="Editar"
                  >
                    <Icons.Edit sx={{ fontSize: 18 }} />
                  </button>
                  <button
                    type="button"
                    onClick={() => askDelete(expense)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Excluir"
                  >
                    <Icons.Delete sx={{ fontSize: 18 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => {
          if (!saving) setModalOpen(false);
        }}
        title={editingExpense ? "Editar Despesa" : "Nova Despesa"}
        icon={<Icons.TrendingDown sx={{ fontSize: 22 }} />}
        headerGradient="gray"
        size="sm"
      >
        <div className="space-y-4">
          <Input
            id="expense-description"
            label="Descrição"
            required
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Ex: Conta de energia"
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              id="expense-amount"
              label="Valor (R$)"
              type="number"
              required
              min={0}
              step={0.01}
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="0,00"
            />
            <Input
              id="expense-date"
              label="Data"
              type="date"
              required
              value={form.expenseDate}
              onChange={(e) => setForm((f) => ({ ...f, expenseDate: e.target.value }))}
            />
          </div>

          <Select
            id="expense-category"
            label="Categoria"
            value={form.category}
            onChange={(value) => setForm((f) => ({ ...f, category: String(value) }))}
            placeholder="Selecionar categoria"
            options={[
              { value: "", label: "Selecionar categoria" },
              ...EXPENSE_CATEGORIES.map((c) => ({ value: c, label: c })),
            ]}
          />

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              id="isRecurrent"
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-900 text-sky-600 focus:ring-sky-500 dark:focus:ring-sky-400"
              checked={form.isRecurrent}
              onChange={(e) => setForm((f) => ({ ...f, isRecurrent: e.target.checked }))}
            />
            <span className="text-sm text-gray-700 dark:text-slate-200">
              Despesa recorrente
            </span>
          </label>

          {form.isRecurrent && (
            <Select
              id="expense-recurrence"
              label="Frequência de recorrência"
              value={form.recurrenceType}
              onChange={(value) =>
                setForm((f) => ({ ...f, recurrenceType: String(value) }))
              }
              options={RECURRENCE_TYPES.map((r) => ({
                value: r.value,
                label: r.label,
              }))}
              searchable={false}
            />
          )}

          <div>
            <label
              htmlFor="expense-notes"
              className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
            >
              Observações
            </label>
            <textarea
              id="expense-notes"
              className="w-full min-w-0 px-4 py-3 rounded-xl text-sm sm:text-base border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 shadow-sm dark:shadow-none focus:outline-none focus:ring-2 focus:ring-sky-500/40 dark:focus:ring-sky-400/40 focus:border-sky-500 dark:focus:border-sky-400 hover:border-gray-400 dark:hover:border-slate-500 transition-[border-color,box-shadow] duration-200 resize-none"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Observações opcionais..."
            />
          </div>

          <FormActions
            onCancel={() => setModalOpen(false)}
            onSubmit={handleSave}
            loading={saving}
            submitType="button"
            submitText={editingExpense ? "Salvar" : "Registrar"}
            cancelText="Cancelar"
          />
        </div>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={Boolean(deleteTarget) && !deleteScopeModalOpen}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete("single")}
        title="Excluir Despesa"
        message={`Tem certeza que deseja excluir "${deleteTarget?.description}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        loading={deleting}
        variant="danger"
      />

      <Modal
        isOpen={deleteScopeModalOpen}
        onClose={() => {
          if (deleting) return;
          setDeleteScopeModalOpen(false);
          setDeleteTarget(null);
        }}
        title="Excluir recorrência"
        icon={<Icons.Warning sx={{ fontSize: 22 }} />}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-slate-300">
            Deseja excluir apenas este lançamento ou este e os próximos da recorrência?
          </p>
          <div className="grid grid-cols-1 gap-2">
            <ActionButton variant="secondary" size="md" onClick={() => handleDelete("single")} loading={deleting}>
              Excluir apenas este
            </ActionButton>
            <ActionButton variant="danger" size="md" onClick={() => handleDelete("future")} loading={deleting}>
              Excluir este e próximos
            </ActionButton>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={editScopeModalOpen}
        onClose={() => {
          if (saving) return;
          setEditScopeModalOpen(false);
          setPendingEditPayload(null);
        }}
        title="Editar recorrência"
        icon={<Icons.Warning sx={{ fontSize: 22 }} />}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-slate-300">
            Deseja aplicar a edição apenas neste lançamento ou neste e nos próximos da recorrência?
          </p>
          <div className="grid grid-cols-1 gap-2">
            <ActionButton variant="secondary" size="md" onClick={() => applyEditScope("single")} loading={saving}>
              Editar apenas este
            </ActionButton>
            <ActionButton variant="primary" size="md" onClick={() => applyEditScope("future")} loading={saving}>
              Editar este e próximos
            </ActionButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};
